import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { waLink } from "../lib/spa";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, Upload } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

function normalisePtPhone(raw) {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.startsWith("00351")) digits = digits.slice(5);
  else if (digits.startsWith("351") && digits.length === 12) digits = digits.slice(3);
  return digits;
}
const PHONE_RE = /^9\d{8}$/;

export default function Candidaturas() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState({ photo1: null, photo2: null, photo3: null });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const bullets = t("applications.profileBullets") || [];

  const validate = ({ name, email, phone }) => {
    const errs = {};
    if (!name.trim()) errs.name = t("applications.requiredError");
    if (!EMAIL_RE.test(email.trim())) errs.email = t("applications.emailError");
    if (!PHONE_RE.test(normalisePtPhone(phone))) errs.phone = t("applications.phoneError");
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(errs.email || errs.phone || errs.name);
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("name", form.name.trim());
      data.append("email", form.email.trim());
      data.append("phone", normalisePtPhone(form.phone));
      data.append("message", form.message.trim());
      if (files.photo1) data.append("photo1", files.photo1);
      if (files.photo2) data.append("photo2", files.photo2);
      if (files.photo3) data.append("photo3", files.photo3);
      await axios.post(`${API_BASE}/api/applications`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      toast.success(t("applications.successTitle"));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("applications.sendError");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="applications-page" className="mx-auto max-w-6xl px-5 pb-24 pt-32 sm:px-8 sm:pt-40">
      <Seo title={`${t("applications.title")} | Magnolia Lunar`} description={t("applications.subtitle")} />
      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("applications.label")}</p>
        <h1 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl lg:text-6xl">
          {t("applications.title")}
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground/72">{t("applications.subtitle")}</p>
      </Reveal>

      <div className="mt-14 grid gap-10 lg:grid-cols-5">
        {/* LEFT: profile description */}
        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="rounded-2xl border border-foreground/8 bg-card p-8">
            <h2 className="font-display text-2xl text-foreground">{t("applications.profileTitle")}</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground/72">{t("applications.profileText")}</p>
            <ul className="mt-6 space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-foreground/82">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm italic text-foreground/72">{t("applications.profileClose")}</p>

            <a
              href={waLink(t("applications.whatsapp"))}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="applications-whatsapp-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/60 bg-gold/5 px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-black"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {t("applications.whatsapp")}
            </a>
          </div>
        </Reveal>

        {/* RIGHT: form or success */}
        <Reveal delay={0.2} className="lg:col-span-3">
          <div className="rounded-2xl border border-gold/20 bg-gradient-to-br from-gold/8 via-transparent to-transparent p-8 sm:p-10">
            {success ? (
              <div data-testid="applications-success" className="text-center py-6">
                <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.5} />
                <h2 className="mt-6 font-display text-3xl text-foreground">{t("applications.successTitle")}</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground/72">{t("applications.successText")}</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="applications-form">
                <h2 className="font-display text-2xl text-foreground">{t("applications.formTitle")}</h2>

                <FieldInput
                  id="appl-name"
                  label={t("applications.name") + " *"}
                  value={form.name}
                  onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                  error={errors.name}
                />
                <FieldInput
                  id="appl-email"
                  type="email"
                  label={t("applications.email") + " *"}
                  value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                  onBlur={() => {
                    setErrors((e) => ({
                      ...e,
                      email: form.email && !EMAIL_RE.test(form.email.trim()) ? t("applications.emailError") : undefined,
                    }));
                  }}
                  error={errors.email}
                />
                <FieldInput
                  id="appl-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={9}
                  placeholder="9XXXXXXXX"
                  label={t("applications.phone") + " *"}
                  value={form.phone}
                  onChange={(v) => {
                    // strip non-digits; enforce max 9 digits
                    const digits = v.replace(/\D/g, "").slice(0, 9);
                    setForm((f) => ({ ...f, phone: digits }));
                  }}
                  onBlur={() => {
                    setErrors((e) => ({
                      ...e,
                      phone: form.phone && !PHONE_RE.test(normalisePtPhone(form.phone)) ? t("applications.phoneError") : undefined,
                    }));
                  }}
                  hint={t("applications.phoneHint")}
                  error={errors.phone}
                />
                <FieldTextarea
                  id="appl-message"
                  label={t("applications.message")}
                  placeholder={t("applications.messagePlaceholder")}
                  value={form.message}
                  onChange={(v) => setForm((f) => ({ ...f, message: v }))}
                />

                <div className="grid gap-3 sm:grid-cols-3">
                  <FieldFile id="appl-photo1" label={t("applications.photo1")} onChange={(f) => setFiles((s) => ({ ...s, photo1: f }))} />
                  <FieldFile id="appl-photo2" label={t("applications.photo2")} onChange={(f) => setFiles((s) => ({ ...s, photo2: f }))} />
                  <FieldFile id="appl-photo3" label={t("applications.photo3")} onChange={(f) => setFiles((s) => ({ ...s, photo3: f }))} accept="image/*,application/pdf" />
                </div>
                <p className="text-xs text-foreground/50">{t("applications.photoHint")}</p>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="applications-submit"
                  className="w-full rounded-full bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] disabled:opacity-60"
                >
                  {submitting ? t("applications.submitting") : t("applications.submit")}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

const FieldInput = ({ id, label, value, onChange, onBlur, type = "text", inputMode, maxLength, placeholder, hint, error }) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-foreground/65">{label}</span>
    <input
      id={id}
      data-testid={id}
      type={type}
      inputMode={inputMode}
      maxLength={maxLength}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      aria-invalid={!!error}
      className={`w-full rounded-lg border bg-background/60 px-4 py-3 text-sm text-foreground outline-none transition-colors focus:border-gold ${
        error ? "border-red-400/60" : "border-foreground/12"
      }`}
    />
    {error ? (
      <span data-testid={`${id}-error`} className="mt-1 block text-xs text-red-400">{error}</span>
    ) : hint ? (
      <span className="mt-1 block text-xs text-foreground/50">{hint}</span>
    ) : null}
  </label>
);

const FieldTextarea = ({ id, label, value, onChange, placeholder }) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-foreground/65">{label}</span>
    <textarea
      id={id}
      data-testid={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={4}
      className="w-full rounded-lg border border-foreground/12 bg-background/60 px-4 py-3 text-sm text-foreground outline-none transition-colors focus:border-gold"
    />
  </label>
);

const FieldFile = ({ id, label, onChange, accept = "image/*" }) => {
  const [name, setName] = useState("");
  return (
    <label htmlFor={id} className="block cursor-pointer">
      <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-foreground/65">{label}</span>
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-foreground/20 bg-background/40 px-3 py-3 text-xs text-foreground/72 hover:border-gold">
        <Upload className="h-4 w-4 text-gold" strokeWidth={1.5} />
        <span className="truncate">{name || "Escolher ficheiro"}</span>
      </div>
      <input
        id={id}
        data-testid={id}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] || null;
          setName(f?.name || "");
          onChange(f);
        }}
      />
    </label>
  );
};
